
import { Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEOMetadata from '@/components/SEOMetadata';
import { Calendar, User, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const Blog = () => {
  const blogPosts = [
    {
      id: 'residential-proxies-travel-industry-ethics',
      title: 'The Quiet Power of Residential Proxies',
      excerpt: 'An insider perspective on how residential proxies are transforming travel pricing transparency and the importance of ethical usage in competitive markets.',
      author: 'NEXX Industry Insights',
      date: '2024-05-20',
      readTime: '7 min read',
      category: 'Industry',
      image: '/lovable-uploads/52ecf367-6211-48aa-82c5-83c88558cc42.png'
    },
    {
      id: 'data-center-security-best-practices',
      title: 'Data Center Security Best Practices for 2024',
      excerpt: 'Explore the latest security measures and protocols that modern data centers are implementing to protect critical infrastructure and sensitive data.',
      author: 'NEXX Technical Team',
      date: '2024-05-15',
      readTime: '8 min read',
      category: 'Security',
      image: '/lovable-uploads/52ecf367-6211-48aa-82c5-83c88558cc42.png'
    },
    {
      id: 'choosing-right-proxy-solution',
      title: 'Choosing the Right Proxy Solution for Your Business',
      excerpt: 'A comprehensive guide to understanding different types of proxy services and how to select the best option for your specific business needs.',
      author: 'NEXX Network Team',
      date: '2024-05-10',
      readTime: '6 min read',
      category: 'Networking',
      image: '/lovable-uploads/52ecf367-6211-48aa-82c5-83c88558cc42.png'
    }
  ];

  return (
    <>
      <SEOMetadata
        title="NEXX Blog | Data Center & Network Infrastructure Insights"
        description="Read the latest insights, updates, and expertise from NEXX TELECOM about data centers, networking, proxy solutions, and infrastructure best practices."
        keywords="data center blog, networking insights, proxy solutions, infrastructure best practices, NEXX TELECOM blog"
        ogTitle="NEXX Blog | Infrastructure Insights & Expertise"
        ogDescription="Latest insights and expertise from NEXX TELECOM about data centers, networking, and infrastructure solutions."
        canonical="https://nexx.net/blog"
      />
      <div className="min-h-screen bg-gray-50">
        <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-12 bg-gradient-to-br from-nexx-green to-green-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            NEXX Blog
          </h1>
          <p className="text-xl text-green-100 max-w-2xl mx-auto">
            Insights, updates, and expertise from the world of data centers, networking, and infrastructure
          </p>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {blogPosts.map((post) => (
              <article
                key={post.id}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
              >
                <div className="aspect-w-16 aspect-h-9">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-48 object-cover"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3">
                    <span className="bg-nexx-green text-white px-2 py-1 rounded-full text-xs font-medium">
                      {post.category}
                    </span>
                    <div className="flex items-center space-x-1">
                      <Calendar className="h-4 w-4" />
                      <span>{new Date(post.date).toLocaleDateString()}</span>
                    </div>
                    <span>{post.readTime}</span>
                  </div>
                  
                  <h2 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2">
                    {post.title}
                  </h2>
                  
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <User className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-600">{post.author}</span>
                    </div>
                    
                    <Link to={`/blog/${post.id}`}>
                      <Button 
                        variant="ghost" 
                        className="text-nexx-green hover:text-green-700 hover:bg-green-50 p-2"
                      >
                        Read More
                        <ArrowRight className="h-4 w-4 ml-1" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

        <Footer />
      </div>
    </>
  );
};

export default Blog;
